/* eslint-disable array-callback-return */
import React, {
  FunctionComponent,
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import styles from "./CheckoutInfo.module.scss";
import { MultiStepProgress } from "components/MultiStepProgress";
import { Collapsible } from "components/basic/Collapsible";
import { SelectComponent as Select } from "components/basic/Select";
import { ButtonRound } from "components/basic/Buttons";
import { CompoundSlider as Slider } from "components/basic/Slider";
import { getApplePayAvailability } from "../../core/applePay";
import { GooglePayButton } from "../../components/GooglePayButton/GooglePayButton";
import { GOOGLE_PAY_LIVE_FOR_ALL } from "../../core/googlePay";
import { Spinner } from "components/basic/Spinner";
import imgVehicle from "@/assets/img/car_template.png";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import { V12Calculator } from "@/pages/Finance/V12Calculator";
import {
  selectBasicInfo,
  fetchBasicInfo,
  fetchFinanceOptions,
  selectFinanceOptions,
  selectFinancePrice,
  selectMaximumDeposit,
  submitFinanceOption,
  selectCreditInformation,
  selectBasicInfoStatus,
  selectDekoOptionStatus,
  selectCreditInfoStatus,
  payAction,
  selectFaqs,
  selectSourcecode,
  selectBasePrice,
  selectPayError,
  clearPayError,
} from "./checkoutInfoSlice";
import { numberWithCommas } from "@/core/helpers/formatHelper";
import { progressInfosFactory } from "@/pages/checkout/constants";
import { CHECKOUT_CONFIG } from "@/pages/checkout/config";
import ReactGA from 'react-ga';
import { trackViewItem, trackAddToWishlist, trackFinanceSelect, trackEmailCapture } from '../../core/helpers/tracking';
import { PRICES } from '../../prices';

const EnquiryForm = React.lazy(
  () => import("@/components/EnquiryForm/EnquiryForm")
);

type Props = {};

const VAT_PRICE = 1.2;
const STANDARD_PLATE_PRICE_INC_VAT = PRICES.platesPair;
const TRANSFER_SERVICE_PRICE_INC_VAT = PRICES.transferService;

const POUND = "\u00A3";
const CURRENT_PAGE_NUMBER = 0;
// Wallet payments. Apple Pay runs on the Opayo (Elavon) Direct rail; the old
// Worldpay route was dropped 2026-08-11 and its code deleted 2026-09-28. Enabled on beta ONLY, by explicit
// hostname allow-list rather than "not www" — an allow-list cannot leak onto an
// unexpected host. Do not switch this to a blanket true until the charge half is
// proven end to end.
//
// LIVE-TEST OPT-IN, added 2026-09-30. MUST STAY BYTE-IDENTICAL to the copy in
// CheckoutPayment.tsx — this flag is deliberately duplicated per-component and
// the two have drifted before. The full reasoning lives in CheckoutPayment.tsx;
// the short version: Elavon confirmed wallets cannot be tested anywhere but
// live, so on www the wallets stay OFF for everyone except a session that opted
// in with ?wallet=test. Still an allow-list; fails closed if storage is blocked.
const WALLET_PAY_ENABLED = (() => {
  if (typeof window === 'undefined') { return false; }
  const host = window.location.hostname;
  if (host === 'beta.newreg.co.uk') { return true; }
  if (host !== 'www.newreg.co.uk') { return false; }
  try {
    if (new URLSearchParams(window.location.search).get('wallet') === 'test') {
      sessionStorage.setItem('nrWalletLiveTest', '1');
    }
    return sessionStorage.getItem('nrWalletLiveTest') === '1';
  } catch (e) {
    // Private mode or blocked storage — fail closed, wallets stay hidden.
    return false;
  }
})();
// Google Pay configured at Opayo 2026-09-25 — see CheckoutPayment.tsx for the
// gateway/merchant ids and the TEST-mode caveat. Keep these two flags in step.
// The earlier note here said Google Pay was off for want of a merchant id and
// because the tokenizationSpecification still named Worldpay; both stopped being
// true on 2026-09-25, and the Worldpay code was deleted on 2026-09-28.
// APPLE PAY LIVE on www for every customer, 2026-10-02 (Jon: "lets get apple pay
// deployed"). Proven on production (P2XYP1790943374) with the price floor and
// discount re-validation live. GOOGLE PAY LIVE on www for every customer,
// 2026-10-06, via GOOGLE_PAY_LIVE_FOR_ALL (core/googlePay.ts). MUST STAY
// BYTE-IDENTICAL in CheckoutInfo.tsx and CheckoutPayment.tsx. Still a host allow-list.
const APPLE_PAY_ENABLED: boolean = WALLET_PAY_ENABLED ||
  (typeof window !== 'undefined' && window.location.hostname === 'www.newreg.co.uk');
const GOOGLE_PAY_ENABLED: boolean = WALLET_PAY_ENABLED || GOOGLE_PAY_LIVE_FOR_ALL;
const FAVE_ENABLED = window.location.hostname !== 'www.newreg.co.uk';

const CheckoutInfo: FunctionComponent<Props> = (props: Props) => {
  const [value, setValue] = useState(30);
  const [availablePurchase, setAvailablePurchase] = useState(true);
  const [sliderDomain, setSliderDomain] = useState<[number, number]>([0, 50]);

  const [registrationMarkPrice, setRegistrationMarkPrice] = useState("0");
  const [dvlaFee, setDvlaFee] = useState("0");
  const [totalPrice, setTotalPrice] = useState(0);
  const [showFinance, setShowFinance] = useState(false);
  const [options, setOption] = useState<Array<string>>([]);
  const [optionKeys, setOptionKeys] = useState<Array<string>>([]);
  const [optionValue, setOptionValue] = useState("");
  const [forceDepositMax, setForceDepositMax] = useState(false);
  const [isDvlaPlate, setIsDvlaPlate] = useState(false);
  const [needVTARegisteredShow, setNeedVTARegisteredShow] = useState(false);
  const [selectedFinanceOption, setSelectedFinanceOption] = useState('');
  const [applePayAvailableCI, setApplePayAvailableCI] = useState(false);
  const [faveAdded, setFaveAdded] = useState(false);

  const [checkoutMessages, setCheckoutMessages] = useState<{ card: string | null; finance: string | null }>({ card: null, finance: null });
  const [copiedCard, setCopiedCard] = useState(false);
  const [copiedFinance, setCopiedFinance] = useState(false);

  const [checkoutOffer, setCheckoutOffer] = useState<{
    plates_policy: string;
    transfer_policy: string;
    postage_policy: string;
    auto_discount_code: string | null;
    display_label: string | null;
    discount_type: string | null;
    discount_price: number | null;
    applies_to: string | null;
  } | null>(null);
  const [checkoutOfferAnyFinance, setCheckoutOfferAnyFinance] = useState<{
    plates_policy: string;
    transfer_policy: string;
    postage_policy: string;
    auto_discount_code: string | null;
    display_label: string | null;
    info_page_notice: string | null;
    discount_type: string | null;
    discount_price: number | null;
    applies_to: string | null;
  } | null>(null);

  const [currentOption, setCurrentOption] = useState<{
    title: string;
    group: string;
    name: string;
    term: string;
    interest: number;
  }>();
  const [_depositAmount, setDepositAmount] = useState(0);

  const enquiryFormRef = useRef(null);
  const hasTrackedBeginCheckout = useRef(false);
  const financeRef = useRef<HTMLDivElement>(null);

  const { sel, registration }: { sel: string; registration: string } =
    useParams() as { sel: string; registration: string };
  const basicInfo = useAppSelector(selectBasicInfo);
  const financeOptions = useAppSelector(selectFinanceOptions);
  const financePrice = useAppSelector(selectFinancePrice);
  const maximumDeposit = useAppSelector(selectMaximumDeposit);
  const creditInformation = useAppSelector(selectCreditInformation);
  const basicStatus = useAppSelector(selectBasicInfoStatus);
  const dekoStatus = useAppSelector(selectDekoOptionStatus);
  const financeStatus = useAppSelector(selectCreditInfoStatus);
  const sourcecode = useAppSelector(selectSourcecode);
  const basePrice = useAppSelector(selectBasePrice);
  const faqs = useAppSelector(selectFaqs);
  const payError    = useAppSelector(selectPayError);
  const dispatch = useAppDispatch();

  const progressInfos = useMemo(
    () => progressInfosFactory({ registration }),
    [registration]
  );
  const progress = useMemo(
    () => progressInfos.map((info) => info.title),
    [progressInfos]
  );

  const navigate = useNavigate();

  const [calculator] = useState(() => new V12Calculator());

  // Wallet availability — show express checkout buttons + pre-select on payment
  // page. Via core/applePay so this works beyond Safari (Apple's JS SDK).
  useEffect(() => {
    if (!APPLE_PAY_ENABLED) return;
    let cancelled = false;
    getApplePayAvailability().then(({ available }) => {
      if (!cancelled && available) setApplePayAvailableCI(true);
    });
    return () => { cancelled = true; };
  }, []);

  const nextPage = (isFinance: boolean) => {
    let url = progressInfos[CURRENT_PAGE_NUMBER + 1].path;
    if (isFinance) {
      url = url + "?finance=true";
    }
    navigate(url);
  };

  const regPreview =
    "https://www.newreg.co.uk/reg.php?reg=" +
    registration +
    "&logo=no_logo&amp;border=border_yes&amp;size=regular-front";

  const nf = new Intl.NumberFormat("en", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  useEffect(() => {
    dispatch(fetchBasicInfo({ sel, registration }));
    dispatch(fetchFinanceOptions({ registration }));
    dispatch(clearPayError());
    document.title = "Information about " + registration.replace("+", " ");
  }, []);

  // Fetch checkout messages — card and finance slots managed from nr-admin.
  // Always-live (no TTL); falls back to cache on Brandy outage.
  useEffect(() => {
    fetch('/api/checkout/get_checkout_messages.php')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) {
          setCheckoutMessages({ card: data.card || null, finance: data.finance || null });
        }
      })
      .catch(() => {});
  }, []);

  // Pre-fetch both offer configs on mount — stored in state for rendering.
  // The correct offer is written to sessionStorage in the Pay Monthly continueCallback,
  // once we know whether the customer selected 0% or general finance.
  useEffect(() => {
    fetch('/api/checkout/checkout_offer.php?trigger=zero_percent')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        setCheckoutOffer(data && data.ok && data.offer ? data.offer : null);
      })
      .catch(() => setCheckoutOffer(null));
    fetch('/api/checkout/checkout_offer.php?trigger=any_finance')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        setCheckoutOfferAnyFinance(data && data.ok && data.offer ? data.offer : null);
      })
      .catch(() => setCheckoutOfferAnyFinance(null));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load saved finance details from sessionStorage
  useEffect(() => {
    try {
      const savedFinanceDetails = sessionStorage.getItem('selectedFinanceDetails');
      if (savedFinanceDetails) {
        const { depositPercent, optionKey } = JSON.parse(savedFinanceDetails);
        if (depositPercent !== undefined) {
          setValue(depositPercent);
        }
        if (optionKey) {
          setOptionValue(optionKey);
        }
      }
    } catch (error) {
      console.error('Error loading saved finance details:', error);
    }
  }, []);

  useEffect(() => {
    const querySel = new URLSearchParams(window.location.search).get('sel');
    if (showFinance && (sel === 'finance' || querySel === 'finance')) {
      scrollToFinance();
    }
  }, [showFinance]);

  useEffect(() => {
    setForceDepositMax(maximumDeposit < 50);
  }, [maximumDeposit]);

  // calculate slider domain
  const calculateSliderDomain = (key: string): [number, number] => {
    if (!financeOptions[key] || !financePrice) return [0, 50];
    
    const option = financeOptions[key];
    const minLoan = typeof option.minLoan === 'string' ? parseFloat(option.minLoan) : option.minLoan;
    const maxLoan = typeof option.maxLoan === 'string' ? parseFloat(option.maxLoan) : option.maxLoan;
    
    // calculate min deposit percent
    let minDepositPercent = (financePrice - maxLoan) / financePrice * 100;
    minDepositPercent = Math.max(0, minDepositPercent);
    
    // calculate max deposit percent
    let maxDepositPercent = (financePrice - minLoan) / financePrice * 100;
    maxDepositPercent = Math.min(100, maxDepositPercent);
    
    // consider maximumDeposit limit
    maxDepositPercent = Math.min(maxDepositPercent, maximumDeposit);
    
    // ensure minDepositPercent <= maxDepositPercent
    if (minDepositPercent > maxDepositPercent) {
      return [0, maximumDeposit > 0 ? Math.floor(maximumDeposit) : 0];
    }
    
    // since the slider is integer step, we need to convert the percentage value to integer
    // for min value, ceil, ensure Credit Amount not exceed maxLoan
    // for max value, floor, ensure Credit Amount not less than minLoan
    
    // calculate min deposit percent
    const ceiledMinDepositPercent = Math.ceil(minDepositPercent);
    
    // check if the min deposit percent is less than maxLoan
    const depositAmountForCeiledMin = (financePrice / 100) * ceiledMinDepositPercent;
    const creditAmountForCeiledMin = financePrice - depositAmountForCeiledMin;
    
    // if the min deposit percent is less than maxLoan, use the original min deposit percent
    let finalMinDepositPercent = creditAmountForCeiledMin <= maxLoan ? ceiledMinDepositPercent : Math.floor(minDepositPercent);
    
    // calculate max deposit percent
    const flooredMaxDepositPercent = Math.floor(maxDepositPercent);
    
    // check if the max deposit percent is less than minLoan
    const depositAmountForFlooredMax = (financePrice / 100) * flooredMaxDepositPercent;
    const creditAmountForFlooredMax = financePrice - depositAmountForFlooredMax;
    
    // if the max deposit percent is less than minLoan, adjust the max deposit percent
    let finalMaxDepositPercent = flooredMaxDepositPercent;
    if (creditAmountForFlooredMax < minLoan) {
      // recalculate to ensure Credit Amount equals minLoan
      const adjustedMaxDepositPercent = ((financePrice - minLoan) / financePrice) * 100;
      // floor, ensure Credit Amount not less than minLoan
      finalMaxDepositPercent = Math.floor(adjustedMaxDepositPercent);
    }
    
    // ensure max deposit percent not exceed 50% （v12 default max deposit percent）
    finalMaxDepositPercent = Math.min(finalMaxDepositPercent, 50);
    
    // ensure min value not greater than max value
    if (finalMinDepositPercent > finalMaxDepositPercent) {
      finalMinDepositPercent = finalMaxDepositPercent;
    }
    
    return [finalMinDepositPercent, finalMaxDepositPercent];
  };

  useEffect(() => {
    const arr: Array<string> = [];
    const arr2: Array<string> = [];
    
    // Filter finance products that meet loan amount requirements
    const validFinanceOptions: { [key: string]: any } = {};
    let hasValidOption = false;
    let savedDepositPercent = value; // default deposit percent
    
    for (let key in financeOptions) {
      const option = financeOptions[key];
      const actualLoanAmount = financePrice;
      
      if (actualLoanAmount >= option.minLoan && actualLoanAmount <= option.maxLoan && (parseFloat(option.interest.toString()) !== 0 || (parseInt(option.term) === 6 && sourcecode === "1" && basePrice === 250))) {
        validFinanceOptions[key] = option;
        arr.unshift(option.name);
        arr2.unshift(key);
        hasValidOption = true;
      }
    }

    if (!hasValidOption && Object.keys(financeOptions).length > 0) {
      // if no valid finance options, hide finance
      setShowFinance(false);
      return;
    }
    
    setOption(arr);
    setOptionKeys(arr2);

    // Try to get saved finance option from sessionStorage
    try {
      const savedFinanceDetails = sessionStorage.getItem('selectedFinanceDetails');
      if (savedFinanceDetails) {
        const { optionKey, depositPercent } = JSON.parse(savedFinanceDetails);
        
        // save cached depositPercent
        if (depositPercent !== undefined) {
          savedDepositPercent = depositPercent;
          setValue(depositPercent);
        }
        
        // Check if saved option is still valid
        if (optionKey && arr2.includes(optionKey)) {
          setOptionValue(optionKey);
          setSelectedFinanceOption(validFinanceOptions[optionKey].name);
          setCurrentOption(validFinanceOptions[optionKey]);
          
          // calculate and set slider domain
          const domain = calculateSliderDomain(optionKey);
          setSliderDomain(domain as [number, number]);
          
          calculateFinanceFigures(optionKey, savedDepositPercent); // use cached depositPercent
          return;
        }
      }
    } catch (error) {
      console.error('Error loading saved finance option:', error);
    }

    // If no saved option or saved option is invalid, use default
    setOptionValue(arr2[0]);
    setSelectedFinanceOption(arr[0]);
    if (arr2.length > 0) {
      const firstKey = arr2[0];
      setCurrentOption(validFinanceOptions[firstKey]);
      
      // calculate and set slider domain
      const domain = calculateSliderDomain(firstKey);
      setSliderDomain(domain as [number, number]);
      
      calculateFinanceFigures(firstKey, savedDepositPercent); // use cached depositPercent
    }
  }, [financeOptions, financePrice, sourcecode, basePrice]);

  // add a useEffect to listen to the change of optionValue, update slider domain
  useEffect(() => {
    if (optionValue) {
      const domain = calculateSliderDomain(optionValue);
      setSliderDomain(domain as [number, number]);
    }
  }, [optionValue, financePrice, maximumDeposit]);

  useEffect(() => {
    const registrationDetails = basicInfo?.registrationDetails;
    if (registrationDetails) {
      if (parseFloat(registrationDetails.display_price || "0") > 0) {
        if (!availablePurchase) {
          setAvailablePurchase(true);
        }
      } else {
        setAvailablePurchase(false);
        return;
      }
    }

    if (
      registrationDetails &&
      ["1", "38", "57", "111", "128", "134", "428", "464"].indexOf(registrationDetails.sourcecode) !== -1 &&
      parseInt(registrationDetails.price) < 25000
    ) {
      setShowFinance(true);
    }

    if (basicInfo?.pricingType === "site_pricing_standard") {
      setRegistrationMarkPrice(
        basicInfo?.registrationDetails.originalPrice ||
          basicInfo?.registrationDetails.price
      );
      setDvlaFee(basicInfo?.registrationDetails.transfer_fee);

      let dotVatPrice = 0;
      if (basicInfo?.registrationDetails.web_vat) {
        dotVatPrice = returnVatOfPriceOnly(
          basicInfo?.registrationDetails.transfer_fee
        );
      }

      const totalNoVatPrice =
        parseFloat(
          basicInfo?.registrationDetails.originalPrice ||
            basicInfo?.registrationDetails.price
        ) + parseFloat(basicInfo?.registrationDetails.transfer_fee);

      const totalVatPrice =
        parseFloat(basicInfo?.registrationDetails.vat) + dotVatPrice;
      setTotalPrice(totalNoVatPrice + totalVatPrice);
    }

    if (basicInfo?.pricingType === "site_pricing_dvla") {
      setIsDvlaPlate(true);
      setRegistrationMarkPrice(basicInfo?.registrationDetails.price);
      setDvlaFee(basicInfo?.registrationDetails.transfer_fee);
      setTotalPrice(
        basicInfo?.registrationDetails.price_plus_vat +
          parseFloat(basicInfo?.registrationDetails.transfer_fee)
      );
    }

    if (basicInfo?.pricingType === "site_pricing_stock") {
      const registrationNoVatPrice = basicInfo?.registrationDetails
        .originalPrice
        ? basicInfo?.registrationDetails.originalPrice
        : basicInfo?.registrationDetails.price;
      setRegistrationMarkPrice(registrationNoVatPrice);
      setDvlaFee(basicInfo?.registrationDetails.transfer_fee);
      const totalNoVatPrice =
        parseFloat(
          basicInfo?.registrationDetails.originalPrice ||
            basicInfo?.registrationDetails.price
        ) + returnPriceMinusVat(basicInfo?.registrationDetails.transfer_fee);
      const totalVatPrice =
        parseFloat(basicInfo?.registrationDetails.vat) +
        returnVatOfPrice(basicInfo?.registrationDetails.transfer_fee);
      setTotalPrice(totalNoVatPrice + totalVatPrice);
    }

    if (
      basicInfo?.registrationDetails.sourcecode !== "134" &&
      basicInfo?.registrationDetails.display_vat &&
      parseFloat(basicInfo?.registrationDetails.display_vat) > 0 &&
      basicInfo?.pricingType !== "site_pricing_dvla"
    ) {
      setNeedVTARegisteredShow(true);
    }
  }, [basicInfo]);
    
  useEffect(()=>{
    ReactGA.pageview(window.location.pathname);
  }, [])

  // Fire view_item once, after basicInfo loads with real price.
  // Signals "user reached the plate price page with purchase intent" — sits above
  // begin_checkout in the GA4 funnel (view_item → begin_checkout → purchase).
  // begin_checkout now fires in checkout/customise/index.tsx once email is validated.
  // Using a ref guard so price updates (VAT etc) don't double-fire.
  useEffect(()=>{
    if (hasTrackedBeginCheckout.current) return;
    const price = basicInfo?.registrationDetails?.price;
    if (!price) return;
    trackViewItem(registration, parseFloat(price));
    hasTrackedBeginCheckout.current = true;
  }, [basicInfo?.registrationDetails?.price])

  const calculateFinanceFigures = (
    key: string,
    depositPercent: number
  ) => {
    const isZeroPct = key && financeOptions[key]
      ? parseFloat(financeOptions[key].interest.toString()) === 0
        || financeOptions[key].interest.toString() === '0'
        || financeOptions[key].interest.toString() === '0.0'
        || financeOptions[key].interest.toString() === '0.00'
      : false;
    // Only add plate cost to financed amount when plates are forced on 0% finance orders
    const includePlates = (isZeroPct && CHECKOUT_CONFIG.includeAcrylicsOnZeroPercentFinance)
      || (isZeroPct && checkoutOffer?.plates_policy === 'forced');
    // Calculate any auto-discount on the plates (e.g. free plates on 0% finance offer)
    const platesDiscount = includePlates && checkoutOffer?.applies_to?.includes('plate') &&
      checkoutOffer?.discount_type && checkoutOffer?.discount_price != null
      ? (checkoutOffer.discount_type === 'percent'
          ? (parseFloat(checkoutOffer.discount_price as any) / 100) * STANDARD_PLATE_PRICE_INC_VAT
          : parseFloat(checkoutOffer.discount_price as any) * 1.2)  // fixed price is ex-VAT
      : 0;
    // Transfer service is NOT in financePrice — add it to the loan when forced, then subtract any discount
    const includeTransfer = isZeroPct && checkoutOffer?.transfer_policy === 'forced';
    const transferDiscount = includeTransfer && checkoutOffer?.applies_to?.includes('transfer') &&
      checkoutOffer?.discount_type && checkoutOffer?.discount_price != null
      ? (checkoutOffer.discount_type === 'percent'
          ? (parseFloat(checkoutOffer.discount_price as any) / 100) * TRANSFER_SERVICE_PRICE_INC_VAT
          : parseFloat(checkoutOffer.discount_price as any) * 1.2)  // fixed price is ex-VAT
      : 0;
    const effectiveFinancePrice = (() => {
      let price = parseFloat(financePrice.toString());
      if (includePlates) price += STANDARD_PLATE_PRICE_INC_VAT - platesDiscount;
      if (includeTransfer) price += TRANSFER_SERVICE_PRICE_INC_VAT - transferDiscount;
      return price;
    })();

    // ensure depositPercent is in the valid range
    const domain = calculateSliderDomain(key);
    const minDepositPercent = domain[0];
    const maxDepositPercent = domain[1];
    
    // since the slider is integer step, ensure depositPercent is integer
    let adjustedDepositPercent = Math.round(depositPercent);
    
    // adjust depositPercent, ensure it is in the valid range
    if (adjustedDepositPercent < minDepositPercent) {
      adjustedDepositPercent = minDepositPercent;
    } else if (adjustedDepositPercent > maxDepositPercent) {
      adjustedDepositPercent = maxDepositPercent;
    }
    
    // if depositPercent exceeds maximumDeposit, use maximumDeposit
    const _despositPercent =
      adjustedDepositPercent > maximumDeposit ? Math.floor(maximumDeposit) : adjustedDepositPercent;
    setValue(_despositPercent);
    // deposit is calculated on financePrice (plate cost added separately to loan, not deposit base)
    let _depositAmount = (financePrice / 100) * _despositPercent;
    
    if (financePrice < 250 && maximumDeposit === 0) {
      _depositAmount = 0;
      setValue(0);
    }
    
    // ensure Credit Amount not less than minLoan
    const option = financeOptions[key];
    const minLoan = typeof option.minLoan === 'string' ? parseFloat(option.minLoan) : option.minLoan;
    let creditAmount = effectiveFinancePrice - _depositAmount;
    
    if (creditAmount < minLoan) {
      // if Credit Amount is less than minLoan, adjust deposit amount
      _depositAmount = Math.max(0, effectiveFinancePrice - minLoan);
      // recalculate depositPercent, ensure it is integer
      const newDepositPercent = Math.floor((_depositAmount / financePrice) * 100);
      setValue(newDepositPercent);
      // use the new depositPercent to recalculate deposit amount, ensure precise
      _depositAmount = (financePrice / 100) * newDepositPercent;
    }
    
    _depositAmount = Math.ceil(parseFloat(_depositAmount.toString()) * 100) / 100;
    setDepositAmount(_depositAmount);
    if (!financeOptions[key]) return;

    const selectedProduct = {
      productId: parseInt(financeOptions[key].productId),
      productGuid: financeOptions[key].productGuid,
      name: financeOptions[key].name,
      minLoan: financeOptions[key].minLoan,
      maxLoan: financeOptions[key].maxLoan,
      months: parseInt(financeOptions[key].term),
      monthlyRate: financeOptions[key].monthlyRate,
      apr: financeOptions[key].interest,
      serviceFee: financeOptions[key].serviceFee,
      documentFee: financeOptions[key].documentFee,
      documentFeeCollectionMonth: financeOptions[key].documentFeeCollectionMonth,
      documentFeeMaximum: financeOptions[key].documentFeeMaximum,
      documentFeeMinimum: financeOptions[key].documentFeeMinimum,
      documentFeePercentage: financeOptions[key].documentFeePercentage,
      optionPeriod: financeOptions[key].optionPeriod,
      deferredPeriod: financeOptions[key].deferredPeriod,
      tag: key,
      altTag: financeOptions[key].altTag,
      settlementFee: financeOptions[key].settlementFee,
      calculationFactor: financeOptions[key].calculationFactor
    };

    const result = calculator.calculate(selectedProduct, effectiveFinancePrice, _depositAmount);
    // Format payment_regular_detail based on payment information
    const months = result.months;
    const initialPayments = result.initialPayments ? result.initialPayments : '0.00';
    const finalPayment = result.finalPayment ? result.finalPayment : '0.00';
    let payment_regular_detail = '';
    if (initialPayments === finalPayment) {
      payment_regular_detail = `${months} x ${POUND}${initialPayments}`;
    } else {
      payment_regular_detail = `${months - 1} x ${POUND}${initialPayments} + ${POUND}${finalPayment}`;
    }
    dispatch(
      submitFinanceOption({
        amount: effectiveFinancePrice,
        product: financeOptions[key].productId,
        deposit: _depositAmount,
        calculationResult: {
          apr: result.apr,
          payment_regular: result.initialPayments,
          payment_regular_detail: payment_regular_detail,
          loan_repayment: (parseFloat(result.balancePayable) + _depositAmount).toFixed(2),
          loan_cost: result.chargeForCredit,
          annual_rate: result.annualRate
        }
      })
    );

    setCurrentOption(financeOptions[key]);
  };

  const isZeroPercentFinance = (): boolean => {
    if (!optionValue || !financeOptions[optionValue]) return false;
    return parseFloat(financeOptions[optionValue].interest.toString()) === 0;
  };

  const sliderExample = () => {
    return (
      <Slider
        domain={sliderDomain}
        value={value}
        onValueChange={(value: Array<number>) => {
          calculateFinanceFigures(optionValue, value[0]);
        }}
      />
    );
  };

  // Auto-detect promotional/discount codes in admin-set messages.
  // Matches uppercase alphanumeric tokens of 4+ chars that contain at least one digit (e.g. PLATE10, SUMMER20).
  const extractCode = (msg: string | null): string | null => {
    if (!msg) return null;
    const tokens = msg.match(/\b[A-Z][A-Z0-9]{3,}\b/g);
    return tokens ? (tokens.find(t => /[0-9]/.test(t)) || null) : null;
  };

  // Renders the card checkout message (managed from nr-admin). Message text is left-aligned; copy button is centred.
  const renderCardMessage = () => {
    if (!checkoutMessages.card) return null;
    const code = extractCode(checkoutMessages.card);
    return (
      <div className="mt-3 mb-1 rounded-xl px-4 py-3 text-sm text-center" style={{ background: '#fefce8', border: '1px solid #fde68a', color: '#92400e' }}>
        <span>{checkoutMessages.card}</span>
        {code && (
          <div className="mt-1 text-center">
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(code); setCopiedCard(true); setTimeout(() => setCopiedCard(false), 2000); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', fontSize: '0.8125rem', textDecoration: 'underline' }}
            >
              {copiedCard ? '✓ Copied!' : `Tap to copy code: ${code}`}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Renders the finance checkout message (managed from nr-admin). Message text is left-aligned; copy button is centred.
  const renderFinanceMessage = () => {
    if (!checkoutMessages.finance) return null;
    const code = extractCode(checkoutMessages.finance);
    return (
      <div className="mt-3 mb-1 rounded-xl px-4 py-3 text-sm text-center" style={{ background: '#fefce8', border: '1px solid #fde68a', color: '#92400e' }}>
        <span>{checkoutMessages.finance}</span>
        {code && (
          <div className="mt-1 text-center">
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(code); setCopiedFinance(true); setTimeout(() => setCopiedFinance(false), 2000); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', fontSize: '0.8125rem', textDecoration: 'underline' }}
            >
              {copiedFinance ? '✓ Copied!' : `Tap to copy code: ${code}`}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Renders the info-page plates notice from the any_finance offer (managed from nr-admin Finance Offers → Info page notice).
  const renderAnyFinanceNotice = () => {
    const notice = checkoutOfferAnyFinance?.info_page_notice;
    if (!notice) return null;
    return (
      <div className="mt-3 mb-1 rounded-xl px-4 py-3 text-sm text-center" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af' }}>
        <span>{notice}</span>
      </div>
    );
  };

  const returnVatOfPriceOnly = (price: string) => {
    const _price = parseFloat(price);
    return _price * VAT_PRICE - _price;
  };

  const returnPriceMinusVat = (price: string) => {
    const _price = parseFloat(price);
    return _price * (1 / VAT_PRICE);
  };

  const returnVatOfPrice = (price: string) => {
    const vat = returnPriceMinusVat(price);
    return parseFloat(price) - vat;
  };

  const scrollToFinance = () => {
    financeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleFave = async () => {
    const reg = registration.replace(/\+/g, ' ');
    try {
      const res = await fetch('/users/wish_list/?regs_for_wishlist=' + encodeURIComponent(reg), { method: 'POST' });
      const data = await res.json();
      if (!data.user) {
        // Not logged in — store intent in session via /referer, then redirect to login
        await fetch('/referer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            referredfor: 'fave',
            regreferred: reg,
            referred: window.location.pathname,
            referraldone: 'false',
          }),
        });
        window.location.href = '/users/';
      } else if (data.added !== undefined) {
        setFaveAdded(true);
        trackAddToWishlist(reg, parseFloat(registrationMarkPrice));
        const g = (window as any).Granterialize;
        if (typeof g === 'function') {
          g('\u2713 ' + reg + ' added to favourites', 4000, 'rounded green lighten-1');
        }
      }
    } catch (e) {}
  };

  const renderPurchaseJsx = () => {
    return (
      <>
        <div className={styles.container}>
          <div className="pt-2">
            <h1 className={`${styles.title} pl-4 mt-6`}>
                Number plates information for {registration.replace("+", " ")}
            </h1>
            <div
              className="grid md:grid-cols-3 sm:grid-cols-1 gap-4 relative"
              style={{ minHeight: 300 }}
            >
              {basicStatus && <Spinner />}
              <div
                className={`md:col-span-2 p-4 ${
                  basicStatus ? "opacity-30" : ""
                }`}
              >
                <div
                  className={`bg-card-bg-grey text-left p-6 h-full grid grid-cols-1 divide-y rounded-2xl overflow-hidden`}
                >
                  {basicInfo?.pricingType === "site_pricing_standard" && (
                    <>
                      <p className="flex justify-between items-center py-4 font-bold text-sm">
                        <span>Registration Mark</span>
                        <span>
                          {POUND}
                          {numberWithCommas(parseFloat(registrationMarkPrice))}
                        </span>
                      </p>
                      {parseFloat(dvlaFee) > 0 && (
                        <p className="flex justify-between items-center py-4 text-sm">
                          <span className="">Compulsory DVLA transfer fee</span>
                          <span>
                            {POUND}
                            {numberWithCommas(parseFloat(dvlaFee))}
                          </span>
                        </p>
                      )}
                      {parseFloat(basicInfo?.registrationDetails.vat) > 0 ? (
                        <p className="flex justify-between items-center py-4 text-sm">
                          <span className="">VAT @ 20%</span>
                          <span>
                            {POUND}
                            {numberWithCommas(
                              parseFloat(basicInfo?.registrationDetails.vat)
                            )}
                          </span>
                        </p>
                      ) : (
                        <></>
                      )}
                      <p className="flex justify-between items-center py-4 text-lime-600 font-bold text-sm">
                        <span className="">Total for Registration Mark</span>
                        <span>
                          {POUND}
                          {numberWithCommas(totalPrice)}
                        </span>
                      </p>
                      {renderCardMessage()}
                    </>
                  )}

                  {basicInfo?.pricingType === "site_pricing_dvla" && (
                    <>
                      <p className="flex justify-between items-center py-4 font-bold text-sm">
                        <span>Registration Mark</span>
                        <span>
                          {POUND}
                          {numberWithCommas(parseFloat(registrationMarkPrice))}
                        </span>
                      </p>
                      <p className="flex justify-between items-center py-4 text-sm">
                        <span className="">Postage</span>
                        <span className="font-bold" style={{ color: "#358639" }}>FREE</span>
                      </p>
                      {parseFloat(basicInfo?.registrationDetails.vat) > 0 && (
                        <p className="flex justify-between items-center py-4 text-sm">
                          <span className="">VAT @ 20%</span>
                          <span>
                            {POUND}
                            {numberWithCommas(
                              parseFloat(basicInfo?.registrationDetails.vat)
                            )}
                          </span>
                        </p>
                      )}
                      <p className="flex justify-between items-center py-4 text-sm">
                        <span className="">DVLA Transfer Fee</span>
                        <span>
                          {POUND}
                          {numberWithCommas(
                            parseFloat(
                              basicInfo?.registrationDetails.transfer_fee
                            )
                          )}
                        </span>
                      </p>
                      <p className="flex justify-between items-center py-4 text-lime-600 font-bold text-sm">
                        <span className="">Total for Registration Mark</span>
                        <span>
                          {POUND}
                          {numberWithCommas(totalPrice)}
                        </span>
                      </p>
                      {renderCardMessage()}
                      {isDvlaPlate && (
                        <p
                          style={{
                            color: "#333",
                            fontSize: "12px",
                            lineHeight: "125%",
                          }}
                        >
                          New Reg will supply the{" "}
                          <b>Certificate of Entitlement</b> for this
                          registration mark, which is valid for 10 years from
                          the date of purchase. If you do not assign this
                          registration mark to a vehicle within 10 years, you
                          must renew it. This is ideal if you have not yet taken
                          delivery of your new vehicle but wish to reserve{" "}
                          {basicInfo?.registrationDetails.registration} in
                          advance. If you are buying{" "}
                          {basicInfo?.registrationDetails.registration} as a
                          gift for someone else, the Certificate of Entitlement
                          acts as a gift certificate and we can even send your
                          communication in plain envelopes so you can keep it a
                          surprise.
                        </p>
                      )}
                      {basicInfo?.registrationDetails.pending && (
                        <p
                          className="nr-pending-caveat"
                          style={{
                            color: "#1b5e20",
                            background: "#e8f5e9",
                            border: "1px solid #a5d6a7",
                            borderRadius: "4px",
                            padding: "8px 10px",
                            fontSize: "12px",
                            lineHeight: "135%",
                          }}
                        >
                          <b>New DVLA release.</b>{" "}
                          {basicInfo?.registrationDetails.registration} is
                          released by DVLA on{" "}
                          <b>{basicInfo?.registrationDetails.release_label}</b>.
                          Order now and we will aim to secure it from DVLA for you
                          on release day. Your order cannot be guaranteed until the
                          registration has been issued to us; if we cannot
                          secure it, you will receive a full refund.
                        </p>
                      )}
                    </>
                  )}
                  {needVTARegisteredShow && (
                    <p>
                      If our donor is not VAT registered, then the price will be
                      amended accordingly.
                    </p>
                  )}
                  {basicInfo?.pricingType === "site_pricing_stock" && (
                    <>
                      <p className="flex justify-between items-center py-4 font-bold text-sm">
                        <span>Registration Mark</span>
                        <span>
                          {POUND}
                          {numberWithCommas(parseFloat(registrationMarkPrice))}
                        </span>
                      </p>
                      {parseFloat(dvlaFee) > 0 && (
                        <p className="flex justify-between items-center py-4 text-sm">
                          <span className="">Compulsory DVLA transfer fee</span>
                          <span>
                            {POUND}
                            {numberWithCommas(parseFloat(dvlaFee))}
                          </span>
                        </p>
                      )}
                      {parseInt(basicInfo?.registrationDetails.vat) > 0 ? (
                        <p className="flex justify-between items-center py-4 text-sm">
                          <span className="">VAT @ 20%</span>
                          <span>
                            {POUND}
                            {numberWithCommas(
                              parseFloat(basicInfo?.registrationDetails.vat)
                            )}
                          </span>
                        </p>
                      ) : (
                        <></>
                      )}
                      <p className="flex justify-between items-center py-4 text-lime-600 font-bold text-sm">
                        <span className="">Total for Registration Mark</span>
                        <span>
                          {POUND}
                          {numberWithCommas(totalPrice)}
                        </span>
                      </p>
                      {renderCardMessage()}
                    </>
                  )}
                  {!basicStatus && (
                    <>
                      {/* Apple Pay above "Pay by card": primary payment option when a
                          card is provisioned in Wallet. Apple's Acceptable Use
                          Guidelines require this once applePayCapabilities has been
                          consulted (see core/applePay.ts); Jon confirmed 2026-09-25. */}
                      {APPLE_PAY_ENABLED && applePayAvailableCI && (
                        <div className="mt-6 text-center" style={{ borderStyle: 'none' }}>
                          <button
                            type="button"
                            style={{ WebkitAppearance: '-apple-pay-button' as any, width: 200, height: 48, border: 'none', cursor: 'pointer', display: 'block', margin: '0 auto' }}
                            onClick={() => {
                              sessionStorage.setItem('nrWalletMethod', 'applepay');
                              dispatch(clearPayError());
                              sessionStorage.removeItem('selectedFinanceDetails');
                              dispatch(payAction({ registration, continueCallback() { nextPage(false); } }));
                            }}
                            aria-label="Pay with Apple Pay"
                          />
                        </div>
                      )}
                      <div
                        className="mt-6 text-center"
                        style={{ borderStyle: "none" }}
                      >
                        <button
                          type="button"
                          className="submit-info-card-btn h-12 rounded-xl font-semibold text-sm text-white transition-all"
                          style={{ width: 200, backgroundColor: "#12b24a", border: "2px solid #12b24a" }}
                          onClick={() => {
                            ReactGA.event({ category: 'Checkout', action: 'pay_by_card_click', label: registration.replace(/\+/g, ' ') });
                            dispatch(clearPayError());
                            sessionStorage.removeItem('selectedFinanceDetails');
                              sessionStorage.removeItem('nrWalletMethod');
                            dispatch(
                              payAction({
                                registration,
                                continueCallback() {
                                  nextPage(false);
                                },
                              })
                            );
                          }}
                        >
                          Pay by card
                        </button>
                      </div>
                      {GOOGLE_PAY_ENABLED && (
                        <div className="mt-3 text-center" style={{ borderStyle: 'none' }}>
                          {/* Google's own button (createButton), same size as "Pay by card" —
                              brand guidelines forbid a home-made Google Pay button. */}
                          <GooglePayButton
                            buttonType="buy"
                            width={200}
                            height={48}
                            buttonRadius={12}
                            onClick={() => {
                              sessionStorage.setItem('nrWalletMethod', 'googlepay');
                              dispatch(clearPayError());
                              sessionStorage.removeItem('selectedFinanceDetails');
                              dispatch(payAction({ registration, continueCallback() { nextPage(false); } }));
                            }}
                          />
                        </div>
                      )}
                      {showFinance && (
                        <div
                          className={`mt-6 text-center ${styles.payByFinance}`}
                          style={{ borderStyle: "none" }}
                        >
                          <button
                            type="button"
                            className="open-finance-bth h-12 rounded-xl font-semibold text-sm text-white transition-all"
                            style={{ width: 200, backgroundColor: "#3b82f6", border: "2px solid #3b82f6" }}
                            onClick={() => {
                              ReactGA.event({ category: 'Checkout', action: 'pay_by_finance_click', label: registration.replace(/\+/g, ' ') });
                              scrollToFinance();
                            }}
                          >
                            Pay by finance
                          </button>
                        </div>
                      )}
                      {payError && (
                        <p className="mt-4 text-sm text-red-600 text-center font-medium" role="alert">
                          {payError}
                        </p>
                      )}
                      {FAVE_ENABLED && (
                      <div className="mt-4 text-center" style={{ borderStyle: "none" }}>
                        <button
                          type="button"
                          className="h-10 rounded-xl font-semibold text-sm transition-all"
                          style={{ width: 200, backgroundColor: "#fff", border: "1.5px solid #fbcfe8", color: "#db2777", cursor: faveAdded ? "default" : "pointer" }}
                          onClick={handleFave}
                          disabled={faveAdded}
                        >
                          {faveAdded ? "\u2665 Saved to favourites" : "\u2661 Save to favourites"}
                        </button>
                      </div>
                      )}
                      <div className="mt-4 text-center" style={{ borderStyle: "none" }}>
                        <button
                          type="button"
                          className="h-10 rounded-xl font-semibold text-sm transition-all"
                          style={{ width: 200, backgroundColor: "#fff", border: "1.5px solid #d1d5db", color: "#374151" }}
                          onClick={() => {
                            (enquiryFormRef.current as any)?.openDialog();
                          }}
                        >
                          Enquire
                        </button>
                      </div>
                    </>
                  )}
                  <EnquiryForm ref={enquiryFormRef} registration={registration} />
                </div>
              </div>
              <div className={`p-4 ${basicStatus ? "opacity-30" : ""}`}>
                <div
                  className={`p-4 bg-card-bg-grey h-full relative items-center flex rounded-2xl overflow-hidden`}
                >
                  <img
                    src={imgVehicle}
                    className="w-full object-cover"
                    alt=""
                  />
                  <img
                    src={regPreview}
                    alt={registration}
                    className={`absolute inset-x-0 m-auto ${styles.previewReg}`}
                  />
                </div>
              </div>
            </div>
          </div>
          {showFinance && (
            <div className="mt-4 mb-8 px-4" id="finance" ref={financeRef} style={{ scrollMarginTop: "80px" }}>
              <button
                type="button"
                className="w-full mb-4 py-3 px-6 rounded-xl font-bold text-base text-white cursor-pointer border-none transition-all active:scale-95"
                style={{ background: "linear-gradient(90deg, #16a34a, #15803d)", boxShadow: "0 2px 8px rgba(22,163,74,0.35)" }}
                onClick={() => {
                  scrollToFinance();
                  calculateFinanceFigures(optionValue, sliderDomain[0]);
                }}
              >
                ✓ Finance available — no deposit to pay!
              </button>


              {/* Offer details panel — shown when there is an active DB-driven offer */}
              {checkoutOffer && (
                <div className="mb-4 rounded-xl border border-indigo-100 bg-indigo-50 px-5 py-4">
                  {checkoutOffer.display_label && (
                    <p className="text-sm font-semibold text-indigo-800 mb-3">{checkoutOffer.display_label}</p>
                  )}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {checkoutOffer.plates_policy === 'forced' && (
                      <div className="flex items-center gap-1.5 text-indigo-700">
                        <span className="text-green-600 font-bold">✓</span>
                        <span>Acrylic plates included</span>
                      </div>
                    )}
                    {checkoutOffer.plates_policy === 'excluded' && (
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <span>✕</span>
                        <span>No physical plates</span>
                      </div>
                    )}
                    {checkoutOffer.transfer_policy === 'forced' && (
                      <div className="flex items-center gap-1.5 text-indigo-700">
                        <span className="text-green-600 font-bold">✓</span>
                        <span>Transfer service included</span>
                      </div>
                    )}
                    {checkoutOffer.postage_policy === 'free' && (
                      <div className="flex items-center gap-1.5 text-indigo-700">
                        <span className="text-green-600 font-bold">✓</span>
                        <span>Free postage</span>
                      </div>
                    )}
                    {checkoutOffer.auto_discount_code && (
                      <div className="flex items-center gap-1.5 text-indigo-700">
                        <span className="text-green-600 font-bold">✓</span>
                        <span>Discount automatically applied</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className={`${styles.financeAvaliable} relative`}>
                  {(dekoStatus || financeStatus) && <Spinner />}
                  <div
                    className={`p-4 md:p-8 bg-card-bg-grey rounded-2xl ${
                      dekoStatus || financeStatus ? "opacity-30" : ""
                    }`}
                  >
                    <div className="">
                      <p className="text-left text-sm font-semibold">
                        Choose deposit amount:
                      </p>
                      {forceDepositMax && maximumDeposit > 0 && (
                        <p className="text-left p-4 bg-amber-100 text-sm bg-amber-200 mt-4">
                          Our minimum financeable figure is £250, with this
                          transaction the{" "}
                          <b>
                            maximum allowable deposit is {maximumDeposit}{" "}
                            percent
                          </b>
                          .
                        </p>
                      )}
                    </div>
                    <div className="mt-8">{sliderExample()}</div>
                    <div className="mt-8">
                      <Select
                        label=""
                        onChange={(e: React.FormEvent<HTMLSelectElement>) => {
                          const index = (
                            e.currentTarget as unknown as { index: number }
                          ).index;
                          setOptionValue(optionKeys[index]);
                          setSelectedFinanceOption(options[index]);
                          setCurrentOption(financeOptions[optionKeys[index]]);
                          calculateFinanceFigures(optionKeys[index], value);
                          trackFinanceSelect(registration, optionKeys[index], value);
                        }}
                        defaultValue={selectedFinanceOption}
                        options={options}
                      />
                    </div>
                    <div className={`text-left font-medium mt-4`}>
                      <p className="flex justify-between items-center py-4 text-sm">
                        <span>Cash Price</span>
                        <span>
                          {POUND}
                          {nf.format(financePrice)}
                        </span>
                      </p>
                      {isZeroPercentFinance() && (CHECKOUT_CONFIG.includeAcrylicsOnZeroPercentFinance || checkoutOffer?.plates_policy === 'forced') && (() => {
                        const hasPlateDiscount = checkoutOffer?.plates_policy === 'forced' &&
                          checkoutOffer?.applies_to?.includes('plate') &&
                          !!checkoutOffer?.discount_type && checkoutOffer?.discount_price != null;
                        const discountAmt = hasPlateDiscount
                          ? (checkoutOffer!.discount_type === 'percent'
                              ? (parseFloat(checkoutOffer!.discount_price as any) / 100) * STANDARD_PLATE_PRICE_INC_VAT
                              : parseFloat(checkoutOffer!.discount_price as any) * 1.2)
                          : 0;
                        const netPrice = STANDARD_PLATE_PRICE_INC_VAT - discountAmt;
                        return (
                          <p className="flex justify-between items-center py-4 text-lime-600 font-semibold text-sm">
                            <span>Standard acrylic plates included</span>
                            <span className="flex items-center gap-2">
                              {hasPlateDiscount && (
                                <span className="line-through text-gray-400 font-normal">+£{STANDARD_PLATE_PRICE_INC_VAT.toFixed(2)}</span>
                              )}
                              <span>+£{netPrice.toFixed(2)}</span>
                            </span>
                          </p>
                        );
                      })()}
                      {isZeroPercentFinance() && checkoutOffer?.plates_policy === 'forced' &&
                        checkoutOffer?.applies_to?.includes('plate') &&
                        checkoutOffer?.discount_type && checkoutOffer?.discount_price != null && (
                        <p className="flex justify-between items-center py-4 text-green-600 font-semibold text-sm">
                          <span>Acrylic plates discount</span>
                          <span>-£{(checkoutOffer.discount_type === 'percent'
                            ? (parseFloat(checkoutOffer.discount_price as any) / 100) * STANDARD_PLATE_PRICE_INC_VAT
                            : parseFloat(checkoutOffer.discount_price as any) * 1.2
                          ).toFixed(2)}</span>
                        </p>
                      )}
                      {isZeroPercentFinance() && checkoutOffer?.transfer_policy === 'forced' && (() => {
                        const hasTransferDiscount = checkoutOffer?.applies_to?.includes('transfer') &&
                          !!checkoutOffer?.discount_type && checkoutOffer?.discount_price != null;
                        const discountAmt = hasTransferDiscount
                          ? (checkoutOffer!.discount_type === 'percent'
                              ? (parseFloat(checkoutOffer!.discount_price as any) / 100) * TRANSFER_SERVICE_PRICE_INC_VAT
                              : parseFloat(checkoutOffer!.discount_price as any) * 1.2)
                          : 0;
                        const netFee = TRANSFER_SERVICE_PRICE_INC_VAT - discountAmt;
                        return (
                          <p className="flex justify-between items-center py-4 text-lime-600 font-semibold text-sm">
                            <span>Transfer service included</span>
                            <span className="flex items-center gap-2">
                              {hasTransferDiscount && (
                                <span className="line-through text-gray-400 font-normal">£{TRANSFER_SERVICE_PRICE_INC_VAT.toFixed(2)}</span>
                              )}
                              <span>£{netFee.toFixed(2)}</span>
                            </span>
                          </p>
                        );
                      })()}
                      {!isZeroPercentFinance() && checkoutOfferAnyFinance?.plates_policy === 'forced' && (() => {
                        const hasPlateDiscount = checkoutOfferAnyFinance?.applies_to?.includes('plate') &&
                          !!checkoutOfferAnyFinance?.discount_type && checkoutOfferAnyFinance?.discount_price != null;
                        const discountAmt = hasPlateDiscount
                          ? (checkoutOfferAnyFinance!.discount_type === 'percent'
                              ? (parseFloat(checkoutOfferAnyFinance!.discount_price as any) / 100) * STANDARD_PLATE_PRICE_INC_VAT
                              : parseFloat(checkoutOfferAnyFinance!.discount_price as any) * 1.2)
                          : 0;
                        const netPrice = STANDARD_PLATE_PRICE_INC_VAT - discountAmt;
                        return (
                          <p className="flex justify-between items-center py-4 text-lime-600 font-semibold text-sm">
                            <span>Standard acrylic plates included</span>
                            <span className="flex items-center gap-2">
                              {hasPlateDiscount && (
                                <span className="line-through text-gray-400 font-normal">+£{STANDARD_PLATE_PRICE_INC_VAT.toFixed(2)}</span>
                              )}
                              <span>+£{netPrice.toFixed(2)}</span>
                            </span>
                          </p>
                        );
                      })()}
                      {!isZeroPercentFinance() && checkoutOfferAnyFinance?.plates_policy === 'forced' &&
                        checkoutOfferAnyFinance?.applies_to?.includes('plate') &&
                        checkoutOfferAnyFinance?.discount_type && checkoutOfferAnyFinance?.discount_price != null && (
                        <p className="flex justify-between items-center py-4 text-green-600 font-semibold text-sm">
                          <span>Acrylic plates discount</span>
                          <span>-£{(checkoutOfferAnyFinance.discount_type === 'percent'
                            ? (parseFloat(checkoutOfferAnyFinance.discount_price as any) / 100) * STANDARD_PLATE_PRICE_INC_VAT
                            : parseFloat(checkoutOfferAnyFinance.discount_price as any) * 1.2
                          ).toFixed(2)}</span>
                        </p>
                      )}
                      {!isZeroPercentFinance() && checkoutOfferAnyFinance?.transfer_policy === 'forced' && (() => {
                        const hasTransferDiscount = checkoutOfferAnyFinance?.applies_to?.includes('transfer') &&
                          !!checkoutOfferAnyFinance?.discount_type && checkoutOfferAnyFinance?.discount_price != null;
                        const discountAmt = hasTransferDiscount
                          ? (checkoutOfferAnyFinance!.discount_type === 'percent'
                              ? (parseFloat(checkoutOfferAnyFinance!.discount_price as any) / 100) * TRANSFER_SERVICE_PRICE_INC_VAT
                              : parseFloat(checkoutOfferAnyFinance!.discount_price as any) * 1.2)
                          : 0;
                        const netFee = TRANSFER_SERVICE_PRICE_INC_VAT - discountAmt;
                        return (
                          <p className="flex justify-between items-center py-4 text-lime-600 font-semibold text-sm">
                            <span>Transfer service included</span>
                            <span className="flex items-center gap-2">
                              {hasTransferDiscount && (
                                <span className="line-through text-gray-400 font-normal">£{TRANSFER_SERVICE_PRICE_INC_VAT.toFixed(2)}</span>
                              )}
                              <span>£{netFee.toFixed(2)}</span>
                            </span>
                          </p>
                        );
                      })()}
                      <p className="flex justify-between items-center py-4 text-sm">
                        <span>Deposit</span>
                        <span>
                          {POUND}
                          {_depositAmount.toFixed(2)}
                        </span>
                      </p>
                      <p className="flex justify-between items-center py-4 text-sm">
                        <span className="">Representative APR</span>
                        <span>{creditInformation?.apr}%</span>
                      </p>
                      {creditInformation?.annual_rate && (
                        <p className="flex justify-between items-center py-4 text-sm">
                          <span className="">Annual Rate of Interest Fixed</span>
                          <span>{creditInformation?.annual_rate}%</span>
                        </p>
                      )}
                      <p className="flex justify-between items-center py-4 text-sm">
                        <span className="">Monthly Payments</span>
                        <span>
                          {creditInformation?.payment_regular_detail}
                        </span>
                      </p>
                      <p className="flex justify-between items-center py-4 text-sm">
                        <span className="">Total Payable</span>
                        <span>
                          {POUND}
                          {creditInformation?.loan_repayment}
                        </span>
                      </p>

                      {/* Checkout message — finance slot (managed from nr-admin, no code change needed) */}
                      {renderFinanceMessage()}
                      {/* Info-page notice — set per-offer in nr-admin Finance Offers → Info page notice */}
                      {renderAnyFinanceNotice()}

                      <div className="flex flex-wrap justify-around py-4 text-sm">
                        <p className="flex items-center justify-around w-60 h-10 text-white bg-blue-500 rounded-xl shadow-lg text-sm font-semibold">
                          <span>
                            {POUND}
                            {nf.format(parseFloat(creditInformation?.loan_repayment || "0") - _depositAmount)}
                          </span>
                          <span>Credit Amount</span>
                        </p>
                        {/* <p className="d-flex align-center justify-around mt-4 sm:mt-0 w-60 h-10 text-white bg-green-600 rounded-lg shadow-lg">
                          <span>
                            {POUND}
                            {parseFloat(
                              creditInformation?.payment_regular
                            ).toFixed(2)}
                          </span>
                          <span>Monthly Amount</span>
                        </p> */}
                      </div>

                      <div
                        className="mt-6 text-center"
                        style={{ borderStyle: "none" }}
                      >
                        <button
                          type="button"
                          className="submit-info-finance-btn h-12 rounded-xl font-semibold text-sm text-white transition-all"
                          style={{ width: "15rem", backgroundColor: "#3b82f6", border: "2px solid #3b82f6" }}
                          onClick={() => {
                            ReactGA.event({ category: 'Checkout', action: 'finance_proceed_click', label: registration.replace(/\+/g, ' ') });
                            dispatch(clearPayError());
                            sessionStorage.setItem('selectedFinanceDetails', JSON.stringify({
                              optionKey: optionValue,
                              depositAmount: _depositAmount,
                              depositPercent: value,
                              isZeroPercent: isZeroPercentFinance(),
                            }));
                            
                            dispatch(
                              payAction({
                                registration,
                                finance: true,
                                continueCallback() {
                                  // Write the correct offer to sessionStorage now that we know
                                  // which finance product the customer selected.
                                  const offerToApply = isZeroPercentFinance()
                                    ? (checkoutOffer ?? checkoutOfferAnyFinance)
                                    : checkoutOfferAnyFinance;
                                  if (offerToApply) {
                                    sessionStorage.setItem('nrCheckoutOffer', JSON.stringify(offerToApply));
                                  } else {
                                    sessionStorage.removeItem('nrCheckoutOffer');
                                  }
                                  nextPage(true);
                                },
                              })
                            );
                          }}
                        >
                          Pay Monthly
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
          )}
        </div>
        <div className={`py-6 bg-card-bg-grey`}>
          <div className={styles.container}>
            <p className={`${styles.title} px-4`}>Frequently Asked Questions</p>
            {faqs?.map((value, index) => {
              if (index % 2 === 0) {
                return (
                  <div key={index} className="flex flex-row flex-wrap">
                    <div className="md:basis-1/2 basis-full px-2 mt-6">
                      <Collapsible
                        text={value.question}
                        graphic="help_outline"
                        type="gray"
                      >
                        <div style={{ padding: "1rem" }}>
                          <span
                            className={styles.faqContent}
                            dangerouslySetInnerHTML={{ __html: value.answer }}
                          ></span>
                        </div>
                      </Collapsible>
                    </div>
                    {faqs[index + 1] && (
                      <div className="md:basis-1/2 basis-full px-2 mt-6">
                        <Collapsible
                          text={faqs[index + 1].question}
                          graphic="help_outline"
                          type="gray"
                        >
                          <div style={{ padding: "1rem" }}>
                            <span
                              className={styles.faqContent}
                              dangerouslySetInnerHTML={{
                                __html: faqs[index + 1].answer,
                              }}
                            ></span>
                          </div>
                        </Collapsible>
                      </div>
                    )}
                  </div>
                );
              }
            })}
          </div>
        </div>
      </>
    );
  };

  const renderRegisterInterestJsx = () => {
    return (
      <>
        <div className={styles.container}>
          <div className="pt-2">
            <h1 className={`${styles.title} pl-4 mt-6`}>
                Number plates information for {registration}
            </h1>
            <div
              className="grid md:grid-cols-3 sm:grid-cols-1 gap-4 relative"
              style={{ minHeight: 300 }}
            >
              {basicStatus && <Spinner />}
              <div
                className={`md:col-span-2 p-4 ${
                  basicStatus ? "opacity-30" : ""
                }`}
              >
                <div
                  className={`bg-card-bg-grey text-left p-6 h-full flex flex-col justify-center align-center rounded-2xl`}
                >
                  <p>
                    This registration not available for purchase, however you
                    can still register interest for <b>{registration}</b> here:
                  </p>
                  <ButtonRound
                    buttonType="button"
                    text="Register Interest"
                    className="h-10 mt-6 w-52"
                    onClick={() => {
                      (enquiryFormRef.current as any)?.openDialog();
                    }}
                  ></ButtonRound>
                  <EnquiryForm
                    ref={enquiryFormRef}
                    registration={registration}
                  ></EnquiryForm>
                </div>
              </div>
              <div className={`p-4 ${basicStatus ? "opacity-30" : ""}`}>
                <div
                  className={`p-4 bg-card-bg-grey h-full relative items-center flex rounded-2xl overflow-hidden`}
                >
                  <img
                    src={imgVehicle}
                    className="w-full object-cover"
                    alt=""
                  />
                  <img
                    src={regPreview}
                    alt={registration}
                    className={`absolute inset-x-0 m-auto ${styles.previewReg}`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={styles.checkoutInfoPage}>
      <MultiStepProgress
        stepNames={progress}
        page={CURRENT_PAGE_NUMBER}
        onStepClick={() => {
          dispatch(clearPayError());
          sessionStorage.removeItem('selectedFinanceDetails');
          sessionStorage.removeItem('nrWalletMethod');
          dispatch(
            payAction({
              registration,
              continueCallback() {
                nextPage(false);
              },
            })
          );
        }}
      />
      {availablePurchase ? renderPurchaseJsx() : renderRegisterInterestJsx()}
    </div>
  );
};

export { CheckoutInfo, CheckoutInfo as default };
